import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { messagingService } from '../../../services/messagingService';
import { useAuth } from '../../../contexts/AuthContext';
import NewTeamGroupChatModal from './NewTeamGroupChatModal';
import { supabase } from '../../../lib/supabase';

const MessagesTab = ({ messages, onSendMessage, teamMembers, currentUser }) => {
  const { user, userProfile } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showStartChatModal, setShowStartChatModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showCreateChatOptions, setShowCreateChatOptions] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [chatType, setChatType] = useState('user'); // 'user' or 'team'
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [showGroupChatModal, setShowGroupChatModal] = useState(false);
  const [isMobileMessageView, setIsMobileMessageView] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageChannelRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const participantChannelRef = useRef(null);

  const emojis = ['👍', '👎', '❤️', '😂', '😮', '😢', '😡', '🎉', '🚀', '💡'];

  // Get available tabs based on user role
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'all', label: 'All', count: getUnreadConversationsCount('all') },
      { id: 'direct', label: 'Direct', count: getUnreadConversationsCount('direct') }
    ];

    // ALWAYS add Teams tab for team group chats - visible to all roles
    baseTabs?.push({ 
      id: 'teams', 
      label: 'Teams', 
      count: getUnreadConversationsCount('teams') 
    });

    return baseTabs;
  };

  // Get unread conversations count for each category
  const getUnreadConversationsCount = (category) => {
    if (!conversations || conversations?.length === 0) return 0;

    let filtered = conversations?.filter(conv => conv?.hasUnreadMessages);

    switch (category) {
      case 'all':
        // Count all conversations with unread messages
        return filtered?.length;
      
      case 'direct':
        // Count direct message conversations with unread messages
        return filtered?.filter(conv => conv?.conversationType === 'direct')?.length;
      
      case 'teams':
        // Count all team group conversations with unread messages (for ALL roles)
        return filtered?.filter(conv => conv?.conversationType === 'group')?.length;
      
      default:
        return 0;
    }
  };

  // Filter conversations based on active tab
  const getFilteredConversations = () => {
    let filtered = conversations || [];

    // Apply tab filter
    switch (activeTab) {
      case 'direct':
        filtered = filtered?.filter(conv => conv?.conversationType === 'direct');
        break;
      case 'teams':
        // Show ALL team group chats for all roles
        filtered = filtered?.filter(conv => conv?.conversationType === 'group');
        break;
      case 'all':
      default:
        // Show all conversations
        break;
    }

    // Apply search filter
    if (searchQuery?.trim()) {
      filtered = filtered?.filter(conv => 
        getConversationTitle(conv)?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    return filtered;
  };

  // Enhanced real-time subscription setup
  useEffect(() => {
    if (selectedConversation?.id) {
      // Clean up existing subscriptions
      if (messageChannelRef?.current) {
        messagingService?.unsubscribe(messageChannelRef?.current);
      }

      console.log(`Setting up real-time subscription for conversation: ${selectedConversation?.id}`);

      // Subscribe to messages for the selected conversation
      messageChannelRef.current = messagingService?.subscribeToMessages(
        selectedConversation?.id,
        (newMessage) => {
          console.log('Received message in TeamChat:', newMessage);

          if (newMessage?.eventType === 'UPDATE') {
            // Handle message status updates
            setConversationMessages(prev => 
              prev?.map(msg => 
                msg?.id === newMessage?.id 
                  ? { ...msg, messageStatus: newMessage?.messageStatus }
                  : msg
              )
            );
          } else {
            // Handle new messages
            setConversationMessages(prev => {
              // Avoid duplicates - check both ID and timestamp
              const exists = prev?.some(msg => 
                msg?.id === newMessage?.id || 
                (msg?.content === newMessage?.content && 
                 Math.abs(new Date(msg?.createdAt) - new Date(newMessage?.createdAt)) < 1000)
              );
              
              if (!exists) {
                console.log('Adding new message to conversation:', newMessage?.id);
                return [...prev, newMessage];
              } else {
                console.log('Duplicate message detected, skipping:', newMessage?.id);
                return prev;
              }
            });

            // Mark conversation as having unread messages if it's not from current user
            if (newMessage?.senderId !== user?.id) {
              console.log('Marking conversation as unread for new message from:', newMessage?.sender?.fullName);
              setConversations(prev => 
                prev?.map(conv => 
                  conv?.id === selectedConversation?.id 
                    ? { ...conv, hasUnreadMessages: true, latestMessage: {
                        content: newMessage?.content,
                        createdAt: newMessage?.createdAt,
                        senderId: newMessage?.senderId,
                        sender: newMessage?.sender
                      }}
                    : conv
                )
              );
            }
          }
        }
      );
    }

    return () => {
      if (messageChannelRef?.current) {
        messagingService?.unsubscribe(messageChannelRef?.current);
      }
    };
  }, [selectedConversation?.id, user?.id]);

  // Enhanced conversation updates subscription
  useEffect(() => {
    if (user) {
      // Clean up existing subscriptions
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
      if (participantChannelRef?.current) {
        messagingService?.unsubscribe(participantChannelRef?.current);
      }

      console.log('Setting up conversation and participant subscriptions');

      // Subscribe to conversation updates
      conversationChannelRef.current = messagingService?.subscribeToConversations(
        (payload) => {
          console.log('Conversation update received:', payload);
          // Refresh conversations when any conversation is updated
          loadConversations();
        }
      );

      // Subscribe to participant updates (for read receipts, etc.)
      participantChannelRef.current = messagingService?.subscribeToConversationParticipants(
        (payload) => {
          console.log('Participant update received:', payload);
          // Refresh conversations when participants are updated
          loadConversations();
        }
      );
    }

    return () => {
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
      if (participantChannelRef?.current) {
        messagingService?.unsubscribe(participantChannelRef?.current);
      }
    };
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  useEffect(() => {
    if (showStartChatModal) {
      if (chatType === 'user') {
        loadUsers();
      } else if (chatType === 'team') {
        loadTeams();
      }
    }
  }, [showStartChatModal, chatType]);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 8000); // Increased timeout for longer error messages
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const users = await messagingService?.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
      showError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      const { data, error } = await supabase?.from('teams')?.select('id, team_name, team_number')?.order('team_name');

      if (error) throw error;
      setTeams(data || []);
    } catch (err) {
      console.error('Error loading teams:', err);
      showError('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadConversations = async () => {
    setLoadingConversations(true);
    setConnectionStatus('connecting');
    try {
      console.log('Loading conversations for user:', user?.id);
      const data = await messagingService?.getConversations();
      console.log('Loaded conversations:', data?.length);
      setConversations(data);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConnectionStatus('disconnected');
      showError('Failed to load conversations: ' + error?.message);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      console.log('Loading messages for conversation:', conversationId);
      const data = await messagingService?.getMessages(conversationId);
      console.log('Loaded messages:', data?.length);
      setConversationMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
      showError('Failed to load messages: ' + error?.message);
    }
  };

  const handleConversationSelect = async (conversation) => {
    console.log('Selecting conversation:', conversation?.id, conversation);
    setSelectedConversation(conversation);
    setError(''); // Clear any existing errors
    setIsMobileMessageView(true); // Switch to message view on mobile
    await loadConversationMessages(conversation?.id);
    
    // Mark conversation as read when user opens it
    try {
      await messagingService?.markConversationAsRead(conversation?.id);
      console.log('Marked conversation as read:', conversation?.id);
      // Update local state to remove unread indicator
      setConversations(prev => 
        prev?.map(conv => 
          conv?.id === conversation?.id 
            ? { ...conv, hasUnreadMessages: false, lastReadAt: new Date()?.toISOString() }
            : conv
        )
      );
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  };

  const handleBackToConversations = () => {
    setIsMobileMessageView(false);
    setSelectedConversation(null);
  };

  const handleStartChat = () => {
    setShowStartChatModal(true);
    setChatType('user'); // Default to user chat
    setSelectedUser('');
    setSelectedTeam('');
    setError('');
  };

  const handleCreateGroupChat = () => {
    setShowGroupChatModal(true);
  };

  const handleGroupChatSuccess = async (conversationId) => {
    await loadConversations();
    
    // Find and select the new conversation
    const newConversation = conversations?.find(c => c?.id === conversationId);
    if (newConversation) {
      handleConversationSelect(newConversation);
    }
  };

  const handleStartTeamChat = async (team) => {
    try {
      setError('');
      const conversationId = await messagingService?.createTeamGroupChat(team?.id);
      
      // Check if this is an existing conversation or a new one
      const existingConv = conversations?.find(c => c?.id === conversationId);
      
      if (existingConv) {
        // Show a message that we're opening the existing chat
        console.log('Opening existing team chat:', team?.team_name);
      }
      
      setShowNewChatModal(false);
      await loadConversations();
      
      // Select the conversation (existing or new)
      const newConv = conversations?.find(c => c?.id === conversationId) || { id: conversationId };
      handleConversationSelect(newConv);
    } catch (error) {
      console.error('Failed to create team conversation:', error);
      showError(error?.message || 'Failed to create team conversation');
    }
  };

  const handleCreateUserConversation = async () => {
    if (!selectedUser) {
      showError('Please select a user to chat with');
      return;
    }

    try {
      setError('');
      const conversation = await messagingService?.createDirectConversation(selectedUser);
      setShowStartChatModal(false);
      setSelectedUser('');
      await loadConversations();
      
      // Select the newly created conversation
      const newConv = conversations?.find(c => c?.id === conversation?.id) || conversation;
      handleConversationSelect(newConv);
    } catch (error) {
      console.error('Failed to create user conversation:', error);
      showError(error?.message || 'Failed to create conversation');
    }
  };

  const handleCreateTeamConversation = async () => {
    if (!selectedTeam) {
      showError('Please select a team to chat with');
      return;
    }

    try {
      setError('');
      const conversationId = await messagingService?.createTeamGroupChat(selectedTeam);
      setShowStartChatModal(false);
      setSelectedTeam('');
      await loadConversations();
      
      // Select the newly created conversation
      const newConv = conversations?.find(c => c?.id === conversationId) || { id: conversationId };
      handleConversationSelect(newConv);
    } catch (error) {
      console.error('Failed to create team conversation:', error);
      showError(error?.message || 'Failed to create team conversation');
    }
  };

  // Enhanced message sending with better error handling
  const handleSendMessage = async () => {
    if (newMessage?.trim() && selectedConversation && !sendingMessage) {
      setSendingMessage(true);
      setError('');

      console.log('Sending message:', {
        conversationId: selectedConversation?.id,
        content: newMessage?.trim(),
        participants: selectedConversation?.participants?.length
      });

      try {
        const newMsg = await messagingService?.sendMessage(
          selectedConversation?.id, 
          newMessage?.trim()
        );
        
        console.log('Message sent successfully:', newMsg?.id, newMsg?.deliveryInfo);
        
        // Add message to local state immediately for better UX
        setConversationMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev?.some(msg => msg?.id === newMsg?.id);
          if (!exists) {
            return [...prev, newMsg];
          }
          return prev;
        });
        
        setNewMessage('');
        setReplyingTo(null);
        
        // Show detailed delivery status to user
        if (newMsg?.deliveryInfo) {
          console.log('Message delivery info:', newMsg?.deliveryInfo);
          
          if (newMsg?.deliveryInfo?.note) {
            // Show info about SMS being disabled
            console.log(newMsg?.deliveryInfo?.note);
          }
          
          // Show success message for successful deliveries
          if (newMsg?.deliveryInfo?.recipients > 0) {
            console.log(`Message delivered to ${newMsg?.deliveryInfo?.recipients} recipient(s) via app`);
          }
        }
        
        // Refresh conversations to update last message
        setTimeout(() => {
          loadConversations();
        }, 500); // Small delay to ensure message is processed

      } catch (error) {
        console.error('Failed to send message:', error);
        
        // More specific error handling
        if (error?.message?.includes('No recipients')) {
          showError('No recipients found in this conversation. Please check if other participants are still active.');
        } else if (error?.message?.includes('Not authenticated')) {
          showError('Your session has expired. Please log in again.');
        } else if (error?.message?.includes('SMS delivery service unavailable')) {
          showError('Message saved but SMS notifications may be delayed. Recipients will see it in the app.');
        } else {
          showError(error?.message || 'Failed to send message. Please try again.');
        }
        
        // Show more detailed error information in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Detailed error:', error);
        }
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file && selectedConversation) {
      // Handle file upload logic here
      console.log('File selected:', file?.name);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);

    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === yesterday?.toDateString()) {
      return 'Yesterday';
    } else {
      return date?.toLocaleDateString();
    }
  };

  const getConversationTitle = (conversation) => {
    if (conversation?.title) return conversation?.title;
    
    if (conversation?.conversationType === 'direct' && conversation?.participants) {
      const otherParticipant = conversation?.participants?.find(p => p?.userId !== user?.id);
      return otherParticipant?.user?.fullName || 'Unknown User';
    }
    
    return 'Group Chat';
  };

  const getConversationAvatar = (conversation) => {
    if (conversation?.conversationType === 'group') {
      return 'G';
    }
    
    const otherParticipant = conversation?.participants?.find(p => p?.userId !== user?.id);
    const name = otherParticipant?.user?.fullName || 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('') || 'U';
  };

  const getLastMessagePreview = (conversation) => {
    if (!conversation?.latestMessage) {
      return 'No messages yet';
    }

    const { latestMessage } = conversation;
    const isOwnMessage = latestMessage?.senderId === user?.id;
    const senderName = isOwnMessage ? 'You' : latestMessage?.sender?.fullName?.split(' ')?.[0] || 'Someone';
    const preview = latestMessage?.content?.length > 30 
      ? `${latestMessage?.content?.substring(0, 30)}...`
      : latestMessage?.content;

    return `${senderName}: ${preview}`;
  };

  // Enhanced message status icon with more detailed information
  const getMessageStatusIcon = (message) => {
    if (message?.senderId !== user?.id) return null;

    switch (message?.messageStatus) {
      case 'sending':
        return <Icon name="Clock" size={12} className="ml-1 text-muted-foreground animate-pulse" title="Sending..." />;
      case 'sent': case'delivered':
        return <Icon name="Check" size={12} className="ml-1 text-green-600" title="Delivered" />;
      case 'partial':
        return <Icon name="AlertTriangle" size={12} className="ml-1 text-yellow-600" title="Partially delivered" />;
      case 'failed':
        return <Icon name="AlertCircle" size={12} className="ml-1 text-destructive" title="Failed to deliver" />;
      default:
        return <Icon name="Check" size={12} className="ml-1" />;
    }
  };

  const filteredConversations = getFilteredConversations();
  const availableTabs = getAvailableTabs();
  const canCreateTeamChats = ['city_official', 'pcf_admin']?.includes(userProfile?.role);

  return (
    <div className="flex h-[calc(100vh-16rem)] bg-card border border-border rounded-lg overflow-hidden">
      {/* Conversations Sidebar - Hidden on mobile when message view is active */}
      <div className={`w-full md:w-80 md:border-r border-border flex flex-col ${
        isMobileMessageView ? 'hidden md:flex' : 'flex'
      }`}>
        <div className="p-3 sm:p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-foreground">Messages</h3>
            
            {/* PCF Admin Create Chat Dropdown */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowCreateChatOptions(!showCreateChatOptions)}
              >
                <Icon name="Plus" size={16} />
              </Button>

              {/* Create Chat Options Dropdown */}
              {showCreateChatOptions && (
                <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-popover border border-border rounded-lg shadow-elevated z-10">
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setShowCreateChatOptions(false);
                        setShowStartChatModal(true);
                        setChatType('user');
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 sm:gap-3 transition-colors"
                    >
                      <Icon name="User" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground text-xs sm:text-sm">Direct Message</p>
                        <p className="text-xs text-muted-foreground hidden sm:block">Chat with individual user</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCreateChatOptions(false);
                        setShowNewChatModal(true);
                        loadTeams();
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 sm:gap-3 transition-colors"
                    >
                      <Icon name="Users" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground text-xs sm:text-sm">Teams Chat</p>
                        <p className="text-xs text-muted-foreground hidden sm:block">Chat with entire team</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCreateChatOptions(false);
                        setShowGroupChatModal(true);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 sm:gap-3 transition-colors"
                    >
                      <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground text-xs sm:text-sm">Custom Chat</p>
                        <p className="text-xs text-muted-foreground hidden sm:block">Create custom group chat</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Filter conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10 text-sm"
            />
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-muted rounded-lg p-1">
            {availableTabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10'
                }`}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <span>{tab?.label}</span>
                  {tab?.count > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      activeTab === tab?.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted-foreground/20 text-muted-foreground'
                    }`}>
                      {tab?.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loadingConversations ? (
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
              {[...Array(3)]?.map((_, i) => (
                <div key={i} className="flex items-center space-x-3 animate-pulse">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-muted rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations?.length === 0 ? (
            <div className="p-3 sm:p-4 text-center">
              <Icon name="MessageCircle" size={28} className="sm:w-8 sm:h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-xs sm:text-sm text-muted-foreground">
                {activeTab === 'all' ? 'No conversations yet' : 
                 activeTab === 'direct' ? 'No direct messages' :
                 activeTab === 'teams' ? 'No team chats' : 'No conversations'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Start a new chat to get started</p>
            </div>
          ) : (
            filteredConversations?.map((conversation) => (
              <div
                key={conversation?.id}
                onClick={() => handleConversationSelect(conversation)}
                className={`flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 cursor-pointer hover:bg-muted transition-colors border-b border-border ${
                  selectedConversation?.id === conversation?.id ? 'bg-muted' : ''
                }`}
              >
                {/* Avatar */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0 relative">
                  <span className="text-xs sm:text-sm font-medium text-primary-foreground">
                    {getConversationAvatar(conversation)}
                  </span>
                  {/* Enhanced Unread message indicator */}
                  {conversation?.hasUnreadMessages && (
                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                  )}
                </div>

                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs sm:text-sm font-medium text-foreground truncate">
                      {getConversationTitle(conversation)}
                    </h3>
                    {conversation?.latestMessage && (
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                        {formatTime(conversation?.latestMessage?.createdAt)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className={`text-xs truncate ${
                      conversation?.hasUnreadMessages 
                        ? 'text-foreground font-medium' 
                        : 'text-muted-foreground'
                    }`}>
                      {getLastMessagePreview(conversation)}
                    </p>
                    
                    {/* Conversation type indicator */}
                    <div className="flex items-center gap-1 ml-2">
                      {conversation?.conversationType === 'group' && (
                        <Icon name="Users" size={12} className="text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Messages Panel - Full width on mobile when active */}
      <div className={`flex-1 flex flex-col ${
        isMobileMessageView ? 'flex' : 'hidden md:flex'
      }`}>
        {selectedConversation ? (
          <>
            {/* Chat Header with Back Button on Mobile */}
            <div className="p-3 sm:p-4 border-b border-border bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                  {/* Back Button - Visible on Mobile Only */}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleBackToConversations}
                    className="md:hidden flex-shrink-0"
                  >
                    <Icon name="ArrowLeft" size={18} />
                  </Button>
                  
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-primary-foreground">
                      {getConversationAvatar(selectedConversation)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground">
                      {getConversationTitle(selectedConversation)}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {selectedConversation?.participants?.length} participant{selectedConversation?.participants?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="icon-sm" 
                    onClick={() => setShowParticipantsModal(true)}
                    title="View Participants"
                  >
                    <Icon name="MoreVertical" size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-muted/5">
              {conversationMessages?.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                  <Icon name="MessageCircle" size={40} className="sm:w-12 sm:h-12 mx-auto text-muted-foreground mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-muted-foreground">No messages yet</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">Start the conversation!</p>
                </div>
              ) : (
                conversationMessages?.map((message, index) => {
                  const isOwnMessage = message?.senderId === user?.id;
                  const showAvatar = !isOwnMessage && (
                    index === 0 || 
                    conversationMessages?.[index - 1]?.senderId !== message?.senderId
                  );

                  return (
                    <div key={`${message?.id}-${index}`} className={`flex items-end space-x-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      {!isOwnMessage && (
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary-foreground">
                            {message?.sender?.fullName?.split(' ')?.map(n => n?.[0])?.join('') || 'U'}
                          </span>
                        </div>
                      )}
                      
                      <div className={`max-w-[75%] sm:max-w-xs lg:max-w-md ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
                        {showAvatar && (
                          <p className="text-xs text-muted-foreground mb-1 px-2">
                            {message?.sender?.fullName || 'Unknown'}
                          </p>
                        )}
                        
                        <div
                          className={`px-3 py-2 rounded-2xl max-w-full break-words ${
                            isOwnMessage
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : 'bg-card border border-border rounded-bl-md'
                          }`}
                        >
                          <p className="text-xs sm:text-sm">{message?.content}</p>
                          <div className={`flex items-center justify-end mt-1 ${
                            isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}>
                            <span className="text-xs">{formatTime(message?.createdAt)}</span>
                            {getMessageStatusIcon(message)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Preview */}
            {replyingTo && (
              <div className="px-3 sm:px-4 py-2 bg-muted border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Reply" size={14} className="text-primary" />
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      Replying to {replyingTo?.sender?.fullName}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setReplyingTo(null)}
                  >
                    <Icon name="X" size={14} />
                  </Button>
                </div>
                <p className="text-xs sm:text-sm text-foreground truncate mt-1">
                  {replyingTo?.content}
                </p>
              </div>
            )}

            {/* Message Input */}
            <div className="p-3 sm:p-4 border-t border-border">
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder={sendingMessage ? "Sending..." : "Type a message..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sendingMessage}
                    className="rounded-full text-sm"
                  />
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={sendingMessage}
                    className="hidden sm:flex"
                  >
                    <Icon name="Paperclip" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    disabled={sendingMessage}
                    className="hidden sm:flex"
                  >
                    <Icon name="Smile" size={16} />
                  </Button>
                  <Button
                    variant="default"
                    size="icon-sm"
                    onClick={handleSendMessage}
                    disabled={!newMessage?.trim() || sendingMessage}
                    className="rounded-full"
                  >
                    {sendingMessage ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Send" size={16} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="mt-2 p-2 bg-popover border border-border rounded-lg shadow-elevated">
                  <div className="flex flex-wrap gap-1">
                    {emojis?.map((emoji, index) => (
                      <button
                        key={index}
                        className="p-2 hover:bg-muted rounded transition-colors"
                        onClick={() => {
                          setNewMessage(prev => prev + emoji);
                          setShowEmojiPicker(false);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <Icon name="MessageCircle" size={48} className="sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">Select a conversation</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                Choose a conversation from the sidebar or start a new one.
              </p>
              <Button variant="default" onClick={handleStartChat} size="sm">
                <Icon name="Plus" size={16} className="mr-2" />
                Start New Chat
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Teams Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-popover border border-border rounded-lg shadow-elevated w-full max-w-md mx-4">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-popover-foreground">
                  Start Team Chat
                </h3>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowNewChatModal(false)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>

            <div className="p-6">
              {loadingTeams ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : teams?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No teams available</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {teams?.map((team) => (
                    <button
                      key={team?.id}
                      onClick={() => handleStartTeamChat(team)}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted transition-micro text-left"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Users" size={20} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{team?.team_name}</p>
                        <p className="text-sm text-muted-foreground">{team?.team_number}</p>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && selectedConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-popover border border-border rounded-lg shadow-elevated w-full max-w-md mx-4">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-popover-foreground">
                  Chat Participants
                </h3>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowParticipantsModal(false)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {selectedConversation?.participants?.map((participant) => (
                  <div
                    key={participant?.userId}
                    className="flex items-center space-x-3 p-3 rounded-lg border border-border"
                  >
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-primary-foreground">
                        {participant?.user?.fullName?.split(' ')?.map(n => n?.[0])?.join('') || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {participant?.user?.fullName}
                      </p>
                      <p className="text-sm text-muted-foreground capitalize">
                        {participant?.user?.role?.replace('_', ' ')}
                      </p>
                    </div>
                    {participant?.userId === user?.id && (
                      <span className="text-xs text-muted-foreground">(You)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Start Chat Modal */}
      {showStartChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Start New Chat</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowStartChatModal(false);
                  setSelectedUser('');
                  setSelectedTeam('');
                  setError('');
                }}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Chat Type Selection */}
              {/*{canCreateTeamChats && (
                <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setChatType('user')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      chatType === 'user' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Direct Message
                  </button>
                  <button
                    onClick={() => setChatType('team')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      chatType === 'team' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Team Chat
                  </button>
                </div>
              )}*/}

              {/* User Selection */}
              {chatType === 'user' && (
                <div>
                  <Select
                    label="Select User to Chat With"
                    placeholder="Choose a user..."
                    searchable
                    loading={loadingUsers}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    options={allUsers?.map(user => ({
                      value: user?.id,
                      label: user?.fullName,
                      description: user?.role
                    }))}
                  />
                </div>
              )}

              {/* Team Selection */}
              {chatType === 'team' && canCreateTeamChats && (
                <div>
                  <Select
                    label="Select Team for Group Chat"
                    placeholder="Choose a team..."
                    searchable
                    loading={loadingTeams}
                    value={selectedTeam}
                    onChange={setSelectedTeam}
                    options={allTeams?.map(team => ({
                      value: team?.id,
                      label: team?.teamName,
                      description: `Team ${team?.teamNumber}`
                    }))}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowStartChatModal(false);
                    setSelectedUser('');
                    setSelectedTeam('');
                    setError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={chatType === 'user' ? handleCreateUserConversation : handleCreateTeamConversation}
                  disabled={chatType === 'user' ? !selectedUser : !selectedTeam}
                >
                  Start Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Group Chat Modal */}
      <NewTeamGroupChatModal
        isOpen={showGroupChatModal}
        onClose={() => setShowGroupChatModal(false)}
        onSuccess={handleGroupChatSuccess}
      />
    </div>
  );
};

export default MessagesTab;